import React, { FC } from 'react';
import { Box, FormLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import styles from './CustomSelect.module.scss';

interface CustomSelectProps {
  name: string;
  inputLabel: string;
  value: string;
  customClass?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  data: string[];
  labelByValue?: Record<string, string>;
  handleChange: (e: SelectChangeEvent<string>) => void;
  placeholder?: string;
}

const CustomSelect: FC<CustomSelectProps> = ({
  name,
  inputLabel,
  value,
  handleChange,
  required = false,
  disabled = false,
  error,
  customClass,
  data,
  labelByValue,
  placeholder,
}) => {
  return (
    <Box
      id={styles.customSelectContainer}
      className={customClass}
    >
      <FormLabel className={styles.customSelectLabel}>
        <Typography variant="h11Semibold">{inputLabel}</Typography>
      </FormLabel>
      <Select
        id={name}
        labelId={name}
        name={name}
        value={value ?? ''}
        required={required}
        disabled={disabled}
        className={`${styles.customInput} ${error ? styles.error : ''} ${disabled ? styles.disabled : ''}`}
        onChange={handleChange}
        IconComponent={ExpandMoreIcon}
        displayEmpty
        MenuProps={{
          PaperProps: {
            elevation: 0,
            sx: {
              boxShadow: '0px 4px 14px 0px #0000001F',
              borderRadius: '10px',
              mt: 0.5,
            },
          },
          MenuListProps: {
            sx: {
              py: 0,
              maxHeight: 'min(50vh, 360px)',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            },
          },
        }}
        renderValue={selected => {
          const placeholderText = placeholder ?? 'Select an option';
          if (selected === '' || selected == null) {
            return (
              <Typography
                component="span"
                variant="h10"
                sx={{ color: '#222B3A' }}
              >
                {placeholderText}
              </Typography>
            );
          }
          const key = selected as string;
          return labelByValue?.[key] ?? key;
        }}
      >
        {data.map((item, index) => (
          <MenuItem
            key={item}
            value={item}
            sx={{
              height: 48,
              minHeight: 48,
              maxHeight: 48,
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              py: 0,
              borderBottom:
                index === data.length - 1
                  ? 'none'
                  : '1px solid var(--system-stroke-buttons, #EAEEF8)',
            }}
          >
            {labelByValue?.[item] ?? item}
          </MenuItem>
        ))}
      </Select>
      {error && (
        <Typography
          variant="h12"
          sx={{ color: '#C8102E', mt: 0.5, ml: 2 }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CustomSelect;
